import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../Context/AuthContext';
import TenantContext from '../Context/TenantContext';
import { getCurrentUser } from '../utils/jwtUtils';
import axios from 'axios';
import LexChatbot from '../Chat/LexChatbot';

const Chatbox = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const { tenant = {} } = useContext(TenantContext) || {};
  const [isOpen, setIsOpen] = useState(false);
  const chatbotRef = useRef(null);
  const [userData, setUserData] = useState(null);
  const [, setLoading] = useState(false);
  const [, setIsSpeaking] = useState(false); // Track speaking state locally

  // Get chat icon from tenant config with fallback
  const chatIcon = tenant?.branding?.chatIcon || '/imgs/robot.png';

  // One-time: capture query param to set manual override in localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const params = new URLSearchParams(window.location.search);
        const qp = params.get('embedChat') ?? params.get('forceEmbeddedChat');
        if (qp !== null) {
          const val = qp === '1' || qp === 'true';
          localStorage.setItem('forceEmbeddedChat', String(val));
        }
      } catch {
        // ignore query parsing errors
      }
    }
  }, []);


  // Get user data from local storage
  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      
      // First try to get user data from token
      const user = getCurrentUser();
      if (user) {
        console.log("User data retrieved from token:", user);
        
        // If phone is missing, fetch complete user data from the server
        if (!user.phone) {
          console.log("Phone number missing in token, fetching complete user data");
          
          // Call the backend to get complete user info using the correct endpoint
          const token = localStorage.getItem('token') || localStorage.getItem('dToken');
          axios.get('/api/v1/user/me', {  // Fixed endpoint
            headers: { Authorization: `Bearer ${token}` }
          })
          .then(response => {
            if (response.data && response.data.success) {
              const completeUser = response.data.user;
              console.log("Complete user data fetched:", completeUser);
              setUserData(completeUser);
            }
          })
          .catch(error => {
            console.error("Error fetching complete user data:", error);
            // Still set the partial user data from token if API call fails
            setUserData(user);
          })
          .finally(() => {
            setLoading(false);
          });
        } else {
          // User data from token is complete, use it directly
          setUserData(user);
          setLoading(false);
        }
      } else {
        console.log("No valid user token found in local storage");
        setLoading(false);
      }
    }
  }, [isAuthenticated]);

  // Note: toggling handled inline on button click

  const handleClose = () => {
    console.log('ChatIcon handleClose called - immediate termination');
    
    // Immediately set closed state
    setIsOpen(false);
    setIsSpeaking(false);
    
    // Force termination without any delays
    if (chatbotRef.current) {
      console.log('Force terminating Lex session immediately');
      try {
        // Use the enhanced terminateLexSession method that includes backend cleanup
        if (typeof chatbotRef.current.terminateLexSession === 'function') {
          chatbotRef.current.terminateLexSession();
        } 
        // Fallback to cleanup method
        else if (typeof chatbotRef.current.cleanup === 'function') {
          chatbotRef.current.cleanup();
        } 
        // Final fallback to close Lex session
        else if (typeof chatbotRef.current.closeLexSession === 'function') {
          chatbotRef.current.closeLexSession();
        } else {
          console.warn('No cleanup methods available on chatbot ref');
        }
      } catch (e) {
        console.warn('Could not terminate Lex session:', e);
      }
    } else {
      console.warn('Could not access chatbot ref');
    }
    
    // No timeout - immediate completion
    console.log('ChatIcon cleanup completed immediately');
  };

  useEffect(() => {
    localStorage.setItem('chatOpenDefault', false);
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, chatbotRef]);

  // Set isOpen to true after login once, using localStorage to track if it has been opened before
  useEffect(() => {
    const chatOpenDefault = localStorage.getItem('chatOpenDefault');
    console.log(chatOpenDefault);
    if (isAuthenticated && chatOpenDefault && chatOpenDefault == 'true') {
      setIsOpen(true);
    }
  }, [isAuthenticated]);

  // Note: previously detected mobile to navigate to /mobile-chat; now unified embedded/modal flow

  // Render nothing if the user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="fixed bottom-6 right-6" style={{ zIndex: isOpen ? 2147483647 : 50 }}>
      <button
        className="relative flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/30 hover:border-white/50 transition-all duration-300 p-4 md:p-5 shadow-2xl hover:shadow-3xl focus:outline-none focus:ring-4 focus:ring-white/40 active:scale-95 hover:scale-105"
        onClick={() => {
          setIsOpen(true);
        }}
        aria-label="Open chat"
      >
        <img src={chatIcon} alt="Chat" className="animate-pulse h-16 md:h-24 w-16 md:w-24 object-contain drop-shadow-lg" />
      </button>
  {/* Embedded chat modal */}
  {isOpen && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center md:items-end md:justify-end md:p-5">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleClose}
          />
          {/* Modal container */}
          <div
            className="relative z-[2010] w-screen h-[100dvh] md:w-full md:max-w-[400px] md:h-[80vh] bg-white/5 backdrop-blur-xl rounded-none md:rounded-2xl overflow-hidden border-0 md:border md:border-white/10 shadow-none md:shadow-2xl"
          >
            <LexChatbot
              ref={chatbotRef}
              onToggle={handleClose}
              userData={userData}
              onSpeakingChange={setIsSpeaking}
              autoFocusInput={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbox;
